import React, { useState, useEffect } from 'react';
import { 
  Shield, 
  Building2, 
  Users, 
  Calendar, 
  Activity,
  Search,
  Eye,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Clock,
  Settings,
  RefreshCw,
  Trash2,
  UserCheck,
  DollarSign,
  MessageSquare,
  Mail,
  CreditCard
} from 'lucide-react';
import { useSuperAdmin } from '../hooks/useSuperAdmin';
import { SuperAdminService, PlatformMetrics, BusinessSummary, UserSummary } from '../services/superAdminService';
import { DeleteBusinessModal } from '../components/DeleteBusinessModal';
import { ImpersonationService } from '../services/impersonationService';
import EnableTrialModal from '../components/EnableTrialModal';
import { useAuth } from '../hooks/useAuth';

// Helper function to format date as "Aug 1st, 2024"
const formatJoinedDate = (dateString: string): string => {
  const date = new Date(dateString);
  const month = date.toLocaleDateString('en-US', { month: 'short' });
  const day = date.getDate();
  const year = date.getFullYear();
  
  // Add ordinal suffix to day
  const getOrdinalSuffix = (day: number): string => {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };
  
  return `${month} ${day}${getOrdinalSuffix(day)}, ${year}`;
};

export default function SuperAdminDashboard() {
  const { isSuperAdmin, loading: authLoading } = useSuperAdmin();
  const { user } = useAuth();
  const [metrics, setMetrics] = useState<PlatformMetrics | null>(null);
  const [businesses, setBusinesses] = useState<BusinessSummary[]>([]);
  const [recentUsers, setRecentUsers] = useState<UserSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<{businesses: BusinessSummary[], users: UserSummary[]} | null>(null);
  const [selectedBusiness, setSelectedBusiness] = useState<any>(null);
  const [showBusinessModal, setShowBusinessModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'businesses'>('overview');
  
  // Filter state
  const [statusFilter, setStatusFilter] = useState<'active' | 'suspended' | 'all'>('active');
  
  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [businessToDelete, setBusinessToDelete] = useState<{ id: string; name: string } | null>(null);
  
  // Trial modal state
  const [showTrialModal, setShowTrialModal] = useState(false);
  const [businessForTrial, setBusinessForTrial] = useState<{ id: string; name: string } | null>(null);
  const [trialLoading, setTrialLoading] = useState(false);

  // Delete business handlers
  const handleDeleteBusiness = (businessId: string, businessName: string) => {
    setBusinessToDelete({ id: businessId, name: businessName });
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setBusinessToDelete(null);
  };

  const handleBusinessDeleted = () => {
    // Refresh the businesses list after deletion
    loadDashboardData();
  };

  // Trial management handlers
  const handleEnableTrial = (businessId: string, businessName: string) => {
    setBusinessForTrial({ id: businessId, name: businessName });
    setShowTrialModal(true);
  };

  const handleCloseTrialModal = () => {
    setShowTrialModal(false);
    setBusinessForTrial(null);
  };

  const handleTrialConfirm = async (trialEndDate: string) => {
    if (!businessForTrial || !user?.id) return;

    setTrialLoading(true);
    try {
      const result = await SuperAdminService.enableTrial(
        businessForTrial.id,
        trialEndDate,
        user.id
      );

      if (result.success) {
        console.log('✅ Trial enabled successfully:', result.message);
        // Refresh the businesses list to show updated status
        loadDashboardData();
        // Show success message (you can add a toast here later)
        alert(result.message);
      } else {
        console.error('❌ Failed to enable trial:', result.message);
        alert(`Failed to enable trial: ${result.message}`);
      }
    } catch (error) {
      console.error('❌ Error enabling trial:', error);
      alert('An unexpected error occurred while enabling the trial');
    } finally {
      setTrialLoading(false);
    }
  };

  // Filter businesses based on status
  const filteredBusinesses = businesses.filter(business => {
    if (statusFilter === 'all') return true;
    return business.status === statusFilter;
  });

  // Impersonate business owner
  const handleImpersonateUser = async (businessId: string, businessName: string) => {
    try {
      console.log('👤 Starting impersonation for business:', businessName);
      
      // Show loading state
      const originalButton = document.querySelector(`[data-impersonate="${businessId}"]`) as HTMLButtonElement;
      if (originalButton) {
        originalButton.innerHTML = '⏳';
        originalButton.disabled = true;
      }
      
      // Get owner data for impersonation
      const result = await SuperAdminService.impersonateBusinessOwner(businessId);
      
      if (result.success && result.ownerData) {
        // Start impersonation
        const impersonateResult = await ImpersonationService.startImpersonation(result.ownerData);
        
        if (impersonateResult.success) {
          // Show success message with more details
          const message = `🎭 Impersonation Started!\n\n` +
            `Business: ${businessName}\n` +
            `User: ${result.ownerData.email}\n` +
            `Name: ${result.ownerData.first_name} ${result.ownerData.last_name}\n\n` +
            `You will now be redirected to their dashboard.\n` +
            `Look for the orange banner to exit impersonation.`;
          
          alert(message);
          
          // Redirect to main app as the impersonated user
          ImpersonationService.reloadToApplyImpersonation();
        } else {
          alert(`❌ Failed to start impersonation:\n${impersonateResult.message}`);
        }
      } else {
        // Enhanced error message with troubleshooting info
        const errorMessage = `❌ Failed to set up impersonation:\n\n` +
          `Issue: ${result.message}\n\n` +
          `This usually means:\n` +
          `• Business has no users assigned\n` +
          `• Owner account was deleted\n` +
          `• Database connection issue\n\n` +
          `Check the console for more details.`;
        
        alert(errorMessage);
      }
    } catch (error) {
      console.error('❌ Error during impersonation:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      alert(`❌ Impersonation failed:\n\n${errorMessage}\n\nCheck the console for details.`);
    } finally {
      // Restore button state
      const originalButton = document.querySelector(`[data-impersonate="${businessId}"]`) as HTMLButtonElement;
      if (originalButton) {
        originalButton.innerHTML = '<svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>';
        originalButton.disabled = false;
      }
    }
  };

  // Load dashboard data
  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const [metricsData, businessesData, usersData] = await Promise.all([
        SuperAdminService.getPlatformMetrics(),
        SuperAdminService.getAllBusinesses(),
        SuperAdminService.getRecentUsers(5)
      ]);
      
      setMetrics(metricsData);
      setBusinesses(businessesData);
      setRecentUsers(usersData);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };




  // Handle search
  const handleSearch = async (query: string) => {
    setSearchQuery(query);
    
    if (!query.trim()) {
      setSearchResults(null);
      return;
    }

    try {
      const results = await SuperAdminService.searchPlatform(query);
      setSearchResults(results);
    } catch (error) {
      console.error('Search error:', error);
    }
  };

  // View business details
  const viewBusinessDetails = async (businessId: string) => {
    try {
      const details = await SuperAdminService.getBusinessDetails(businessId);
      setSelectedBusiness(details);
      setShowBusinessModal(true);
    } catch (error) {
      console.error('Error fetching business details:', error);
    }
  };

  useEffect(() => {
    if (!authLoading && isSuperAdmin) {
      loadDashboardData();
    }
  }, [authLoading, isSuperAdmin]);



  // Redirect if not super admin
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (!isSuperAdmin) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <Shield className="h-16 w-16 text-red-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-100';
      case 'inactive': return 'text-yellow-600 bg-yellow-100';
      case 'suspended': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getHealthColor = (health: string) => {
    switch (health) {
      case 'healthy': return 'text-green-600';
      case 'warning': return 'text-yellow-600';
      case 'critical': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-purple-600 to-purple-700 flex items-center justify-center shadow-lg">
              <Shield className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Super Admin Dashboard</h1>
              <p className="text-gray-600">Platform oversight and management</p>
            </div>
          </div>
          <button 
            onClick={loadDashboardData}
            className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="mb-8">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'overview'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Activity className="h-4 w-4" />
                <span>Overview</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('businesses')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'businesses'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Building2 className="h-4 w-4" />
                <span>Businesses ({metrics?.totalBusinesses || 0})</span>
              </div>
            </button>
          </nav>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        </div>
      ) : (
        <>
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <>
              {/* Platform Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Building2 className="h-5 w-5 text-blue-600" />
                </div>
                <span className={`text-sm font-medium ${getHealthColor(metrics?.systemHealth || 'healthy')}`}>
                  {metrics?.systemHealth || 'Unknown'}
                </span>
              </div>
              <div className="space-y-1">
                <h3 className="text-sm font-medium text-gray-600">Total Businesses</h3>
                <p className="text-2xl font-bold text-gray-900">{metrics?.totalBusinesses || 0}</p>
                <p className="text-xs text-gray-500">{metrics?.activeBusinesses || 0} active</p>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <div className="h-10 w-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <Users className="h-5 w-5 text-green-600" />
                </div>
                <TrendingUp className="h-4 w-4 text-gray-400" />
              </div>
              <div className="space-y-1">
                <h3 className="text-sm font-medium text-gray-600">Total Users</h3>
                <p className="text-2xl font-bold text-gray-900">{metrics?.totalUsers || 0}</p>
                <p className="text-xs text-gray-500">{metrics?.activeUsers || 0} active</p>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <Calendar className="h-5 w-5 text-purple-600" />
                </div>
                <Activity className="h-4 w-4 text-gray-400" />
              </div>
              <div className="space-y-1">
                <h3 className="text-sm font-medium text-gray-600">Total Bookings</h3>
                <p className="text-2xl font-bold text-gray-900">{metrics?.totalBookings || 0}</p>
                <p className="text-xs text-gray-500">{metrics?.monthlyBookings || 0} this month</p>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <div className="h-10 w-10 rounded-lg bg-orange-100 flex items-center justify-center">
                  <Activity className="h-5 w-5 text-orange-600" />
                </div>
                <Settings className="h-4 w-4 text-gray-400" />
              </div>
              <div className="space-y-1">
                <h3 className="text-sm font-medium text-gray-600">System Health</h3>
                <p className={`text-2xl font-bold capitalize ${getHealthColor(metrics?.systemHealth || 'healthy')}`}>
                  {metrics?.systemHealth || 'Healthy'}
                </p>
                <p className="text-xs text-gray-500">All systems operational</p>
              </div>
            </div>
          </div>

          {/* Search Bar */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 mb-8">
            <div className="flex items-center space-x-4">
              <div className="flex-1 relative">
                <Search className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search businesses or users..."
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Search Results */}
            {searchResults && (
              <div className="mt-6 space-y-4">
                {searchResults.businesses.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Businesses</h4>
                    <div className="space-y-2">
                      {searchResults.businesses.map((business) => (
                        <div key={business.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div>
                            <p className="font-medium text-gray-900">{business.name}</p>
                            <p className="text-sm text-gray-600">{business.owner_email}</p>
                          </div>
                          <button 
                            onClick={() => viewBusinessDetails(business.id)}
                            className="flex items-center space-x-1 px-3 py-1 text-sm text-purple-600 hover:bg-purple-50 rounded-md transition-colors"
                          >
                            <Eye className="h-4 w-4" />
                            <span>View</span>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {searchResults.users.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Users</h4>
                    <div className="space-y-2">
                      {searchResults.users.map((user) => (
                        <div key={user.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div>
                            <p className="font-medium text-gray-900">{user.first_name} {user.last_name}</p>
                            <p className="text-sm text-gray-600">{user.email} • {user.business_name}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Recent Businesses and Users */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Recent Businesses */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Recent Businesses</h3>
              </div>
              <div className="p-6">
                {businesses.slice(0, 5).map((business) => (
                  <div key={business.id} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <div className="h-8 w-8 rounded-lg bg-blue-100 flex items-center justify-center">
                          <Building2 className="h-4 w-4 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{business.name}</p>
                          <p className="text-sm text-gray-600">{business.owner_email}</p>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(business.status)}`}>
                        {business.status}
                      </span>
                      <button 
                        onClick={() => viewBusinessDetails(business.id)}
                        className="text-purple-600 hover:text-purple-700 transition-colors"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Users */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Recent Users</h3>
              </div>
              <div className="p-6">
                {recentUsers.map((user) => (
                  <div key={user.id} className="flex items-center space-x-3 py-3 border-b border-gray-100 last:border-0">
                    <div className="h-8 w-8 rounded-lg bg-green-100 flex items-center justify-center">
                      <Users className="h-4 w-4 text-green-600" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{user.first_name} {user.last_name}</p>
                      <p className="text-sm text-gray-600">{user.email}</p>
                      <p className="text-xs text-gray-500">{user.business_name}</p>
                    </div>
                    <div className="text-xs text-gray-500">
                      {formatJoinedDate(user.created_at)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
            </>
          )}



          {/* Businesses Tab */}
          {activeTab === 'businesses' && (
            <div>
              {/* Modern Header */}
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">All Businesses</h2>
                  <p className="text-gray-600 mt-1">Manage and monitor all platform businesses</p>
                </div>
                <button 
                  onClick={loadDashboardData}
                  className="flex items-center space-x-2 px-4 py-2.5 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 shadow-sm"
                >
                  <RefreshCw className="h-4 w-4 text-gray-600" />
                  <span className="font-medium text-gray-700">Refresh</span>
                </button>
              </div>

              {/* Status Filter */}
              <div className="flex items-center space-x-4 mb-6">
                <span className="text-sm font-medium text-gray-700">Filter by status:</span>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setStatusFilter('active')}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                      statusFilter === 'active'
                        ? 'bg-green-100 text-green-700 border border-green-200'
                        : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    Active
                  </button>
                  <button
                    onClick={() => setStatusFilter('suspended')}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                      statusFilter === 'suspended'
                        ? 'bg-red-100 text-red-700 border border-red-200'
                        : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    Suspended
                  </button>
                  <button
                    onClick={() => setStatusFilter('all')}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                      statusFilter === 'all'
                        ? 'bg-purple-100 text-purple-700 border border-purple-200'
                        : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    All
                  </button>
                </div>
                <span className="text-sm text-gray-500">
                  {filteredBusinesses.length} of {businesses.length} businesses
                </span>
              </div>

              {/* Modern Business Cards Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {filteredBusinesses.map((business) => (
                  <div key={business.id} className="group bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-lg hover:border-gray-200 transition-all duration-300">
                    {/* Header with Icon and Status */}
                    <div className="flex items-start justify-between mb-6">
                      <div className="flex items-center space-x-4">
                        <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-sm group-hover:shadow-md transition-shadow">
                          <Building2 className="h-6 w-6 text-white" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 group-hover:text-purple-600 transition-colors">
                            {business.name}
                          </h3>
                          <p className="text-sm text-gray-500 mt-1">
                            {business.trial_enabled ? (
                              <span className="flex items-center space-x-2">
                                <span className="text-blue-600 font-medium">Trial</span>
                                {business.trial_end_date && (
                                  <span className="text-xs text-gray-400">
                                    • Ends {formatJoinedDate(business.trial_end_date)}
                                  </span>
                                )}
                              </span>
                            ) : (
                              business.type || 'Business'
                            )}
                          </p>
                        </div>
                      </div>
                      <span className={`inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded-full shadow-sm ${getStatusColor(business.status)}`}>
                        {business.status}
                      </span>
                    </div>
                    
                    {/* Metrics Section */}
                    <div className="space-y-3 mb-6">
                      <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-2">
                          <Users className="h-4 w-4 text-gray-500" />
                          <span className="text-sm text-gray-600">Active Resources</span>
                        </div>
                        <span className="font-semibold text-gray-900">{business.user_count}</span>
                      </div>
                      <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-2">
                          <DollarSign className="h-4 w-4 text-gray-500" />
                          <span className="text-sm text-gray-600">Sales</span>
                        </div>
                        <span className="font-semibold text-gray-900">
                          ${business.sales_amount?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
                        </span>
                      </div>
                      
                      {/* Integrations Section */}
                      <div className="py-2 px-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-2 mb-2">
                          <Settings className="h-4 w-4 text-gray-500" />
                          <span className="text-sm text-gray-600">Integrations</span>
                        </div>
                        <div className="flex items-center space-x-3">
                          <div className={`flex items-center space-x-1 ${business.integrations?.gmail ? 'text-green-600' : 'text-gray-400'}`}>
                            <Mail className="h-3 w-3" />
                            <span className="text-xs font-medium">Gmail</span>
                          </div>
                          <div className={`flex items-center space-x-1 ${business.integrations?.twilio ? 'text-blue-600' : 'text-gray-400'}`}>
                            <MessageSquare className="h-3 w-3" />
                            <span className="text-xs font-medium">SMS</span>
                          </div>
                          <div className={`flex items-center space-x-1 ${business.integrations?.stripe ? 'text-purple-600' : 'text-gray-400'}`}>
                            <CreditCard className="h-3 w-3" />
                            <span className="text-xs font-medium">Pay</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-2">
                          <Clock className="h-4 w-4 text-gray-500" />
                          <span className="text-sm text-gray-600">Joined</span>
                        </div>
                        <span className="font-semibold text-gray-900">
                          {formatJoinedDate(business.created_at)}
                        </span>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex space-x-2">
                      <button
                        onClick={() => viewBusinessDetails(business.id)}
                        className="flex-1 flex items-center justify-center space-x-2 px-4 py-2.5 bg-purple-50 text-purple-600 rounded-xl hover:bg-purple-100 hover:text-purple-700 transition-all duration-200 font-medium"
                      >
                        <Eye className="h-4 w-4" />
                        <span>View Details</span>
                      </button>
                      
                      {/* Enable Trial Button - Only show for suspended businesses */}
                      {business.status === 'suspended' && (
                        <button
                          onClick={() => handleEnableTrial(business.id, business.name)}
                          className="flex items-center justify-center space-x-1 px-3 py-2.5 bg-blue-50 text-blue-600 rounded-xl hover:bg-blue-100 hover:text-blue-700 transition-all duration-200 font-medium"
                          title="Enable Trial"
                        >
                          <Clock className="h-4 w-4" />
                          <span className="text-xs">Trial</span>
                        </button>
                      )}
                      
                      <button
                        onClick={() => handleImpersonateUser(business.id, business.name)}
                        className="flex items-center justify-center px-3 py-2.5 bg-emerald-50 text-emerald-600 rounded-xl hover:bg-emerald-100 hover:text-emerald-700 transition-all duration-200"
                        title="Impersonate User"
                        data-impersonate={business.id}
                      >
                        <UserCheck className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteBusiness(business.id, business.name)}
                        className="flex items-center justify-center px-3 py-2.5 bg-red-50 text-red-600 rounded-xl hover:bg-red-100 hover:text-red-700 transition-all duration-200"
                        title="Delete Business"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Modern Empty State */}
              {filteredBusinesses.length === 0 && (
                <div className="text-center py-16">
                  <div className="h-20 w-20 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-6">
                    <Building2 className="h-10 w-10 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {businesses.length === 0 ? 'No businesses found' : `No ${statusFilter} businesses found`}
                  </h3>
                  <p className="text-gray-600 max-w-md mx-auto">
                    {businesses.length === 0 
                      ? 'There are no businesses in the system yet. Businesses will appear here once they register on the platform.'
                      : `No businesses match the "${statusFilter}" filter. Try selecting a different filter or check if businesses exist with other statuses.`
                    }
                  </p>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Business Detail Modal */}
      {showBusinessModal && selectedBusiness && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Business Details</h3>
                <button 
                  onClick={() => setShowBusinessModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  ×
                </button>
              </div>
            </div>
            <div className="p-6 space-y-6">
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Basic Information</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Name:</span>
                    <p className="font-medium">{selectedBusiness.name}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Plan:</span>
                    <p className="font-medium">{selectedBusiness.planName || 'No Plan'}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Email:</span>
                    <p className="font-medium">{selectedBusiness.email}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Phone:</span>
                    <p className="font-medium">{selectedBusiness.phone}</p>
                  </div>
                </div>
              </div>

              {selectedBusiness.metrics && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Metrics</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-2xl font-bold text-gray-900">{selectedBusiness.metrics.userCount}</p>
                      <p className="text-sm text-gray-600">Active Resources</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-2xl font-bold text-gray-900">
                        ${selectedBusiness.metrics.salesAmount?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
                      </p>
                      <p className="text-sm text-gray-600">Sales</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-2xl font-bold text-gray-900">{selectedBusiness.metrics.serviceCount}</p>
                      <p className="text-sm text-gray-600">Services</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-2xl font-bold text-gray-900">{selectedBusiness.metrics.customerCount}</p>
                      <p className="text-sm text-gray-600">Customers</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Integrations Section */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Integrations</h4>
                <div className="grid grid-cols-3 gap-3">
                  <div className={`p-3 rounded-lg border ${selectedBusiness.integrations?.gmail ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                    <div className="flex items-center space-x-2">
                      <Mail className={`h-4 w-4 ${selectedBusiness.integrations?.gmail ? 'text-green-600' : 'text-gray-400'}`} />
                      <span className={`text-sm font-medium ${selectedBusiness.integrations?.gmail ? 'text-green-700' : 'text-gray-500'}`}>
                        Gmail
                      </span>
                    </div>
                    <p className={`text-xs mt-1 ${selectedBusiness.integrations?.gmail ? 'text-green-600' : 'text-gray-400'}`}>
                      {selectedBusiness.integrations?.gmail ? 'Connected' : 'Not Connected'}
                    </p>
                  </div>
                  <div className={`p-3 rounded-lg border ${selectedBusiness.integrations?.twilio ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'}`}>
                    <div className="flex items-center space-x-2">
                      <MessageSquare className={`h-4 w-4 ${selectedBusiness.integrations?.twilio ? 'text-blue-600' : 'text-gray-400'}`} />
                      <span className={`text-sm font-medium ${selectedBusiness.integrations?.twilio ? 'text-blue-700' : 'text-gray-500'}`}>
                        SMS
                      </span>
                    </div>
                    <p className={`text-xs mt-1 ${selectedBusiness.integrations?.twilio ? 'text-blue-600' : 'text-gray-400'}`}>
                      {selectedBusiness.integrations?.twilio ? 'Connected' : 'Not Connected'}
                    </p>
                  </div>
                  <div className={`p-3 rounded-lg border ${selectedBusiness.integrations?.stripe ? 'bg-purple-50 border-purple-200' : 'bg-gray-50 border-gray-200'}`}>
                    <div className="flex items-center space-x-2">
                      <CreditCard className={`h-4 w-4 ${selectedBusiness.integrations?.stripe ? 'text-purple-600' : 'text-gray-400'}`} />
                      <span className={`text-sm font-medium ${selectedBusiness.integrations?.stripe ? 'text-purple-700' : 'text-gray-500'}`}>
                        Payments
                      </span>
                    </div>
                    <p className={`text-xs mt-1 ${selectedBusiness.integrations?.stripe ? 'text-purple-600' : 'text-gray-400'}`}>
                      {selectedBusiness.integrations?.stripe ? 'Connected' : 'Not Connected'}
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Owner Information</h4>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="font-medium">{selectedBusiness.users?.first_name} {selectedBusiness.users?.last_name}</p>
                  <p className="text-sm text-gray-600">{selectedBusiness.users?.email}</p>
                  <p className="text-xs text-gray-500">Joined {formatJoinedDate(selectedBusiness.users?.created_at)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Business Modal */}
      {showDeleteModal && businessToDelete && (
        <DeleteBusinessModal
          isOpen={showDeleteModal}
          onClose={handleCloseDeleteModal}
          businessId={businessToDelete.id}
          businessName={businessToDelete.name}
          onDeleted={handleBusinessDeleted}
        />
      )}

      {/* Enable Trial Modal */}
      {showTrialModal && businessForTrial && (
        <EnableTrialModal
          isOpen={showTrialModal}
          onClose={handleCloseTrialModal}
          onConfirm={handleTrialConfirm}
          businessName={businessForTrial.name}
          isLoading={trialLoading}
        />
      )}
    </div>
  );
} 